// src/utils/membership.ts
//
// Stav členství a typ člena z dokumentu users (krok 8 redesignu, backlog
// #10 / #25 / #29). Dosud měla každá obrazovka vlastní kopii:
//   - users.membership[rok] === "ZAPLACENO" (dashboard, PaymentMembership,
//     getUserCategory, MembersList, AdminRegistrations, AdminLiveResults,
//     AdminOverview – ta jediná bez toUpperCase)
//   - getMemberType v AdminRegistrations (Žena / Junior / Člen LKS / Nečlen)
//     s hranicí juniora, která se nikdy nenačetla
//   - getAgeCategory v AdminRegistrations (fallback 18) a computeMemberCategory
//     v MembersList (natvrdo 16 / 49 / 59)
// Věkové hranice sem přicházejí zvenčí jako CategoryLimits z utils/categories.ts
// (useCategoryDefaults / categoryLimitsOf) – modul nemá žádný vlastní fallback.
// Nic se tu nezapisuje; pole dokumentů users se nemění.

import type { DocumentData } from "firebase/firestore";
import type { CategoryLimits } from "./categories";

// ─────────────────────────────────────────────────────────────
//  Stav členství
// ─────────────────────────────────────────────────────────────

export type MembershipStatus = "paid" | "unpaid";

/** Hodnota users.membership[rok], která znamená zaplaceno (zapisuje Cloud Function / admin). */
export const MEMBERSHIP_PAID_VALUE = "ZAPLACENO";

/** Popisky stavu pro odznaky a export (shodné s dosavadním zobrazením v MembersList). */
export const MEMBERSHIP_STATUS_LABELS: Record<MembershipStatus, string> = {
  paid: "ZAPLACENO",
  unpaid: "NEZAPLACENO",
};

/** Část dokumentu users, se kterou modul pracuje (DocumentData vyhovuje). */
export interface MembershipSource {
  /** Členství podle roku: { "2026": "ZAPLACENO", … }. */
  membership?: unknown;
}

export interface MemberTypeSource extends MembershipSource {
  /** Rok narození; number (nebo čtyřmístný řetězec), jinak je věk neznámý. */
  birthYear?: unknown;
  /** Pohlaví ("žena" / "ŽENA"…); velikost písmen nerozhoduje. */
  gender?: unknown;
}

type UserLike = DocumentData | MembershipSource | null | undefined;

/** Uložená hodnota membership[rok] (cokoli), nebo undefined. */
function membershipValue(userDoc: UserLike, year: number | string): unknown {
  if (!userDoc || typeof userDoc !== "object") return undefined;
  const membership = (userDoc as MembershipSource).membership;
  if (!membership || typeof membership !== "object") return undefined;
  return (membership as Record<string, unknown>)[String(year)];
}

/**
 * "paid", když membership[rok] je "ZAPLACENO" (velikost písmen nerozhoduje –
 * 1:1 s dashboardem a getUserCategory); vše ostatní včetně chybějícího
 * dokumentu / pole / nečekaného typu je "unpaid".
 */
export function membershipStatus(
  userDoc: UserLike,
  year: number | string
): MembershipStatus {
  const value = membershipValue(userDoc, year);
  return typeof value === "string" &&
    value.toUpperCase() === MEMBERSHIP_PAID_VALUE
    ? "paid"
    : "unpaid";
}

/** membershipStatus(...) === "paid". */
export function isMembershipPaid(
  userDoc: UserLike,
  year: number | string
): boolean {
  return membershipStatus(userDoc, year) === "paid";
}

/**
 * Všechny roky, které se v membership dokumentů vyskytují (klíče "YYYY",
 * bez ohledu na hodnotu), sloučené s `extra` (např. aktuální rok, ať je ve
 * výběru i bez plateb), bez duplicit, sestupně. Pro výběr minulých let
 * v MembersList (backlog #25).
 */
export function membershipYears(
  users: ReadonlyArray<UserLike>,
  extra: ReadonlyArray<number> = []
): number[] {
  const years = new Set<number>();
  extra.forEach((y) => {
    if (Number.isInteger(y)) years.add(y);
  });
  users.forEach((u) => {
    if (!u || typeof u !== "object") return;
    const membership = (u as MembershipSource).membership;
    if (!membership || typeof membership !== "object") return;
    Object.keys(membership as Record<string, unknown>).forEach((key) => {
      if (/^\d{4}$/.test(key)) years.add(parseInt(key, 10));
    });
  });
  return Array.from(years).sort((a, b) => b - a);
}

// ─────────────────────────────────────────────────────────────
//  Typ člena a věková kategorie
// ─────────────────────────────────────────────────────────────

const isWoman = (gender: unknown): boolean =>
  typeof gender === "string" && gender.toUpperCase() === "ŽENA";

/**
 * Věk v daném roce (rok − rok narození). Rok narození number nebo čtyřmístný
 * řetězec; chybějící, 0 (AdminRegistrations doplňoval `birthYear || 0`) nebo
 * nečíselný → null (věk neznámý).
 */
export function ageInYear(birthYear: unknown, year: number): number | null {
  if (typeof birthYear === "number") {
    return Number.isFinite(birthYear) && birthYear > 0 ? year - birthYear : null;
  }
  if (typeof birthYear === "string" && /^\s*\d{4}\s*$/.test(birthYear)) {
    return year - parseInt(birthYear, 10);
  }
  return null;
}

/** Hodnoty sloupce „Typ člena" (AdminRegistrations, export „Typ člena"). */
export type MemberTypeLabel = "Žena" | "Junior" | "Člen LKS" | "Nečlen";

/**
 * Typ člena pro rok závodu – 1:1 s getMemberType z AdminRegistrations (pořadí
 * pravidel i texty včetně „Člen LKS", aby export zůstal stejný): žena je vždy
 * „Žena", pak „Junior" podle limits.juniorMax (dřív hranice, která se nikdy
 * nenačetla – backlog #10), jinak podle zaplaceného členství v daném roce.
 */
export function memberTypeLabel(
  user: MemberTypeSource,
  year: number,
  limits: CategoryLimits
): MemberTypeLabel {
  if (isWoman(user.gender)) return "Žena";
  const age = ageInYear(user.birthYear, year);
  if (age !== null && age <= limits.juniorMax) return "Junior";
  return isMembershipPaid(user, year) ? "Člen LKS" : "Nečlen";
}

/** Věková kategorie závodníka (sloupec „Kategorie" přihlášek, „Typ" v seznamu střelců). */
export type AgeCategoryLabel = "Junior" | "Žena" | "Muž" | "Senior" | "Veterán";

/**
 * Věková kategorie pro daný rok – 1:1 s getAgeCategory z AdminRegistrations
 * a computeMemberCategory z MembersList, jen s hranicemi z CategoryLimits
 * místo 18 / 16 / 49 / 59 natvrdo: junior (věk ≤ juniorMax) má přednost
 * i u žen, žena bez ohledu na věk, muži podle veteranMin / seniorMin.
 * Jediná změna: neznámý rok narození u muže vrací null (dřív vyšel
 * „Veterán", protože věk byl 2026) – obrazovka ukáže pomlčku.
 */
export function ageCategoryLabel(
  user: { birthYear?: unknown; gender?: unknown },
  year: number,
  limits: CategoryLimits
): AgeCategoryLabel | null {
  const age = ageInYear(user.birthYear, year);
  if (age !== null && age <= limits.juniorMax) return "Junior";
  if (isWoman(user.gender)) return "Žena";
  if (age === null) return null;
  if (age >= limits.veteranMin) return "Veterán";
  if (age >= limits.seniorMin) return "Senior";
  return "Muž";
}
